import { ref, watch, computed, getCurrentInstance } from "@vue/composition-api";
import axios from "axios";
import { formatDate, formatCurrency } from "@/@core/utils/filter";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import api from "@/services/api";
import { decrypt } from "@/utils/crypto";

export default function useInvoiceList() {
    const internalInstance = getCurrentInstance();
    const showCustomLoader = () => {
        return internalInstance?.proxy?.$showCustomLoader();
    };

    const toast = useToast();
    const refInvoiceListTable = ref(null);
    const invoiceList = ref([]);
    const paymentList = ref([]);
    const invoiceMap = ref({}); // map number => id

    // Table configuration
    const tableColumns = [
        { key: "sl", label: "SL", sortable: false },
        { key: "tnx_id", label: "Transaction No.", sortable: true },
        { key: "create_date", label: "Payment Date", sortable: true },
        { key: "ref", label: "Reference", sortable: true },
        { key: "credit", label: "Amount (In)", sortable: true },
        { key: "debit", label: "Amount (Out)", sortable: true },
        { key: "payment_mode", label: "Payment Mode", sortable: true },
        { key: "received_by_name", label: "Received By", sortable: true },
        { key: "action", label: "Download", sortable: false },
    ];

    // State management
    const perPage = ref(25);
    const totalInvoiceRecords = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [25, 50, 100, 300, 500, 1000];
    const searchQuery = ref("");
    const sortBy = ref("payment_date");
    const isSortDirDesc = ref(true);

    const dataMeta = computed(() => ({
        from: perPage.value * (currentPage.value - 1) + 1,
        to: Math.min(perPage.value * currentPage.value, totalInvoiceRecords.value),
        of: totalInvoiceRecords.value,
    }));


    const refetchData = () => {
        loadPaymentData();
    };

    // Optimize the watch effect to prevent unnecessary API calls
    watch(
        [currentPage, perPage, searchQuery],
        () => {
            loadPaymentData();
        },
        { deep: true },
    );

    const loadPaymentData = async () => {
        const userData = decrypt(localStorage.getItem("userData"));
        const username = userData ? userData.username : null;
        const loader = showCustomLoader();

        try {
            const response = await api.get(
                `/selfcare/api/v1/payment/list/?mime_id=${username}&limit=${perPage.value}&offset=${(currentPage.value - 1) * perPage.value}`
            );

            paymentList.value = response.data.transactions || [];
            totalInvoiceRecords.value = response.data.total_count || 0;

            loader?.hide();
        } catch (error) {
            loader?.hide();
            toast({
                component: ToastificationContent,
                props: {
                    title: "Error loading payments",
                    text: error.message || "Unknown error",
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                },
            });
        }
    };


    const loadInvoiceData = () => {
        const userData = decrypt(localStorage.getItem("userData"));
        const username = userData ? userData.username : null;

        (async () => {
            try {
                const response = await api.post("/api/v1/mime_invoice/list/", {
                    searchString: username,
                    searchFor: "username",
                    date_range_start: "",
                    date_range_end: "",
                });

                // ✅ FIX এখানে
                invoiceList.value = response.data.data || [];

                // ✅ map build
                invoiceMap.value = {};

                invoiceList.value.forEach((inv) => {
                    invoiceMap.value[inv.number?.trim()] = inv.id;
                });

            } catch (error) {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Error fetching invoice list",
                        text:
                            error.response?.data?.message ||
                            error.message ||
                            "Unknown error",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });

                invoiceList.value = [];
            }
        })();
    };

    const sortedInvoiceData = computed(() => {
        const key = sortBy.value;
        const desc = isSortDirDesc.value;

        return [...invoiceList.value].sort((a, b) => {
            if (!a[key] || !b[key]) return 0;

            const aVal = a[key].toString().toLowerCase();
            const bVal = b[key].toString().toLowerCase();

            const result = aVal.localeCompare(bVal);
            return desc ? -result : result;
        });
    });

    // helpers to get download ID or type
    const getDownloadType = (payment) => {
        // Receipt
        if (payment.particular === "Customer Payment") {
            return {
                type: "receipt",
                id: payment.payment_id
            };
        }

        // Invoice
        const ref = payment.ref?.trim();

        if (ref && invoiceMap.value[ref]) {
            return {
                type: "invoice",
                id: invoiceMap.value[ref]
            };
        }

        return null;
    };

    const downloadPDF = async (type, id) => {
        if (type === "invoice") {
            return downloadInvoice(id);
        }

        if (type === "receipt") {
            return downloadReceipt(id);
        }
    };

    const downloadInvoice = async (id) => {
        try {
            const loader = showCustomLoader();

            const response = await api.get(
                `/api/v1/mime_invoice/get_invoice_download/${id}/`,
                { responseType: "blob" }
            );

            loader?.hide();

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `invoice_${id}.pdf`);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Invoice download failed", error);
        }
    };


    const downloadReceipt = async (paymentId) => {
        try {
            const loader = showCustomLoader();

            // 1️⃣ Get payment details first
            const detailsRes = await api.get(`/mime_sales/api/v1/payments/details/${paymentId}`);
            let payload = detailsRes.data.result;

            // ✅ Convert payment_amount to string
            payload.payment_amount = payload.payment_amount.toString();

            // 2️⃣ Call receipt download API
            const response = await api.post(
                `/api/v1/mime_invoice/receipt_download/`,
                payload,
                { responseType: "blob" }
            );

            loader?.hide();

            // 3️⃣ Download file
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `receipt__${payload.receipt_id}.pdf`);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            loader?.hide();

            toast({
                component: ToastificationContent,
                props: {
                    title: "Error downloading receipt",
                    text:
                        error.response?.data?.message ||
                        error.message ||
                        "Unknown error",
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                },
            });
        }
    };


    return {
        sortedInvoiceData,
        paymentList,
        invoiceList,
        loadInvoiceData,
        tableColumns,
        perPage,
        currentPage,
        totalInvoiceRecords,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refInvoiceListTable,
        refetchData,
        getDownloadType,
        downloadPDF,
        loadPaymentData,
    };
}
